// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — GAAIM Compliance Viewer
// Route: /compliance
// apps/web/src/routes/compliance.tsx
//
// Enterprise-gated tamper-proof audit chain viewer. Shows GAAIM events with
// hash chain verification, L0/L1 compliance levels, and promote-to-L1 action.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useSubscription, normalizeTier, meetsMinTier } from '../lib/api/billing';
import {
  useGaaimEvents,
  useGaaimStats,
  useGaaimChainStatus,
  useGaaimPromote,
  type GaaimEvent,
  type EventFilters,
} from '../lib/api/compliance';

export const Route = createFileRoute('/compliance')({
  component: CompliancePage,
});

// ─── Event type config ─────────────────────────────────────────────────────

const EVENT_TYPE_CONFIG: Record<string, { icon: string; color: string; category: string }> = {
  CommitAuthored:       { icon: 'C', color: C.cyan,   category: 'Version Control' },
  PrOpened:             { icon: 'P', color: C.violet,  category: 'Version Control' },
  PrReviewed:           { icon: 'R', color: C.violet,  category: 'Version Control' },
  PrMerged:             { icon: 'M', color: C.neon,    category: 'Version Control' },
  ReviewCompleted:      { icon: 'V', color: C.neon,    category: 'Version Control' },
  TestExecuted:         { icon: 'T', color: C.blue,    category: 'Version Control' },
  BuildCompleted:       { icon: 'B', color: C.blue,    category: 'Version Control' },
  DeploymentRecorded:   { icon: 'D', color: C.amber,   category: 'Version Control' },
  AiPromptSubmitted:    { icon: 'Q', color: C.pink,    category: 'AI Workflow' },
  AiResponseReceived:   { icon: 'A', color: C.pink,    category: 'AI Workflow' },
  AiSuggestionAccepted: { icon: '+', color: C.neon,    category: 'AI Workflow' },
  AiSuggestionRejected: { icon: '-', color: C.coral,   category: 'AI Workflow' },
  CodegenApplied:       { icon: '>', color: C.amber,   category: 'AI Workflow' },
  DependencyUpdated:    { icon: 'U', color: C.cyan,    category: 'Supply Chain' },
  SecurityScanned:      { icon: 'S', color: C.coral,   category: 'Supply Chain' },
};

const ALL_EVENT_TYPES = Object.keys(EVENT_TYPE_CONFIG);

// ─── Main Component ─────────────────────────────────────────────────────────

function CompliancePage() {
  const { data: subscription } = useSubscription();
  const currentTier = normalizeTier(subscription?.tier);
  const hasAccess = meetsMinTier(currentTier, 'Enterprise');

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<EventFilters>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: stats, loading: statsLoading, refetch: refetchStats } = useGaaimStats();
  const { data: chain } = useGaaimChainStatus();
  const { data: eventsPage, loading: eventsLoading, refetch: refetchEvents } = useGaaimEvents(page, filters);
  const { promote, loading: promoteLoading } = useGaaimPromote();

  const countTotal = useCounter(stats?.totalEvents ?? 0, 600, 200);
  const countL0 = useCounter(stats?.byLevel?.l0 ?? 0, 500, 300);
  const countL1 = useCounter(stats?.byLevel?.l1 ?? 0, 500, 400);

  return (
    <EnginePageShell
      title="Compliance"
      subtitle="GAAIM AUDIT CHAIN"
      icon="&#x1f6e1;"
      accentColor={C.amber}
      statusLabel={chain?.chainIntact ? 'CHAIN INTACT' : chain ? 'CHAIN BROKEN' : 'LOADING'}
      statusColor={chain?.chainIntact ? C.neon : chain ? C.coral : C.dim}
    >
      {/* ── Tier gate ──────────────────────────────────────────────────── */}
      {!hasAccess ? (
        <GlassPanel accent={C.amber} delay={0.2} style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>&#x1f6e1;</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>
            GAAIM Compliance Chain
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 12, lineHeight: 1.6, maxWidth: 500, margin: '0 auto 20px' }}>
            Tamper-proof hash-chain audit trail for every AI interaction, code commit, review, and deployment.
            SHA-256 linked events with L0/L1 compliance levels, human review promotion, and chain integrity verification.
          </div>
          <div style={{
            display: 'inline-block', fontSize: 10, fontFamily: F.mono, letterSpacing: '0.1em',
            color: C.amber, background: `${C.amber}15`, border: `1px solid ${C.amber}30`,
            borderRadius: 6, padding: '6px 16px',
          }}>
            REQUIRES ENTERPRISE PLAN
          </div>
        </GlassPanel>
      ) : (
        <>
          {/* ── Metrics ──────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            <MetricCard label="Total Events" value={countTotal} icon="&#x1f6e1;" color={C.amber}
              tooltip={<div>Total GAAIM events in the compliance chain.</div>} />
            <MetricCard label="L0 Pending" value={countL0} icon="&#x23f3;" color={C.coral}
              tooltip={<div>Events awaiting human review for L1 promotion.</div>} />
            <MetricCard label="L1 Verified" value={countL1} icon="&#x2714;" color={C.neon}
              tooltip={<div>Events promoted to L1 after human review.</div>} />
            <MetricCard
              label="Chain Status"
              value={chain?.chainIntact ? 'INTACT' : chain ? 'BROKEN' : '...'}
              icon={chain?.chainIntact ? '&#x1f512;' : '&#x26a0;'}
              color={chain?.chainIntact ? C.neon : C.coral}
              tooltip={<div>{chain?.totalLinks ?? 0} linked events. {chain?.chainIntact ? 'No tampering detected.' : `Chain broken at event ${chain?.brokenAt}`}</div>}
            />
          </div>

          {/* ── Category breakdown ────────────────────────────────────── */}
          {stats && stats.totalEvents > 0 && (
            <GlassPanel accent={C.violet} delay={0.3} style={{ padding: 14, marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>CATEGORIES</div>
                <CategoryPill label="Version Control" count={stats.byCategory.versionControl} color={C.cyan} total={stats.totalEvents} />
                <CategoryPill label="AI Workflow" count={stats.byCategory.aiWorkflow} color={C.pink} total={stats.totalEvents} />
                <CategoryPill label="Supply Chain" count={stats.byCategory.supplyChain} color={C.coral} total={stats.totalEvents} />
              </div>
            </GlassPanel>
          )}

          {/* ── Filters ──────────────────────────────────────────────── */}
          <GlassPanel accent={C.blue} delay={0.35} style={{ padding: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>FILTER</span>
              <select
                value={filters.eventType ?? ''}
                onChange={e => { setFilters(f => ({ ...f, eventType: e.target.value || undefined })); setPage(1); }}
                style={filterSelectStyle}
              >
                <option value="">All Types</option>
                {ALL_EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select
                value={filters.complianceLevel ?? ''}
                onChange={e => { setFilters(f => ({ ...f, complianceLevel: e.target.value || undefined })); setPage(1); }}
                style={filterSelectStyle}
              >
                <option value="">All Levels</option>
                <option value="L0">L0 — Pending</option>
                <option value="L1">L1 — Verified</option>
              </select>
              {(filters.eventType || filters.complianceLevel) && (
                <button onClick={() => { setFilters({}); setPage(1); }} style={{
                  background: 'transparent', border: `1px solid ${C.faint}`, color: C.dim,
                  fontFamily: F.mono, fontSize: 9, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                }}>CLEAR</button>
              )}
            </div>
          </GlassPanel>

          {/* ── Event list ────────────────────────────────────────────── */}
          <SectionLabel color={C.amber}>Event Chain</SectionLabel>
          {eventsLoading || statsLoading ? (
            <GlassPanel delay={0.4}><LoadingShimmer height={300} /></GlassPanel>
          ) : eventsPage && eventsPage.items.length > 0 ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {eventsPage.items.map((evt) => (
                  <EventRow
                    key={evt.id}
                    event={evt}
                    expanded={expandedId === evt.id}
                    onToggle={() => setExpandedId(expandedId === evt.id ? null : evt.id)}
                    onPromote={async () => {
                      const ok = await promote(evt.id);
                      if (ok) { refetchEvents(); refetchStats(); }
                    }}
                    promoteLoading={promoteLoading}
                  />
                ))}
              </div>

              {/* Pagination */}
              {eventsPage.totalCount > eventsPage.pageSize && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    style={pageBtnStyle(page > 1)}
                  >PREV</button>
                  <span style={{ fontSize: 11, fontFamily: F.mono, color: C.muted, padding: '6px 12px' }}>
                    {page} / {Math.ceil(eventsPage.totalCount / eventsPage.pageSize)}
                  </span>
                  <button
                    disabled={page * eventsPage.pageSize >= eventsPage.totalCount}
                    onClick={() => setPage(p => p + 1)}
                    style={pageBtnStyle(page * eventsPage.pageSize < eventsPage.totalCount)}
                  >NEXT</button>
                </div>
              )}
            </>
          ) : (
            <GlassPanel accent={C.amber} delay={0.4}>
              <EmptyState
                icon="&#x1f6e1;"
                title="No compliance events yet"
                subtitle="GAAIM events are emitted automatically as AI interactions, commits, and reviews occur."
              />
            </GlassPanel>
          )}
        </>
      )}
    </EnginePageShell>
  );
}

// ─── EventRow ──────────────────────────────────────────────────────────────

function EventRow({ event: evt, expanded, onToggle, onPromote, promoteLoading }: {
  event: GaaimEvent;
  expanded: boolean;
  onToggle: () => void;
  onPromote: () => void;
  promoteLoading: boolean;
}) {
  const cfg = EVENT_TYPE_CONFIG[evt.eventType] || { icon: '?', color: C.dim, category: 'Unknown' };
  const isL1 = evt.complianceLevel === 'L1';
  const time = new Date(evt.createdAt);

  return (
    <div style={{
      background: C.surface, border: `1px solid ${expanded ? `${cfg.color}30` : C.faint}`,
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Summary row */}
      <div
        onClick={onToggle}
        style={{
          display: 'grid', gridTemplateColumns: '32px 110px 1.2fr 0.8fr 60px 70px 100px',
          gap: 10, padding: '10px 14px', alignItems: 'center', cursor: 'pointer',
        }}
      >
        {/* Type icon */}
        <div style={{
          width: 28, height: 28, borderRadius: 6,
          background: `${cfg.color}15`, border: `1px solid ${cfg.color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontFamily: F.mono, fontWeight: 700, color: cfg.color,
        }}>
          {cfg.icon}
        </div>

        {/* Timestamp */}
        <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
          {time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
          {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
        </div>

        {/* Event type */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>{evt.eventType}</div>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}>{cfg.category}</div>
        </div>

        {/* Subject */}
        <div style={{ fontSize: 11, fontFamily: F.mono, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {evt.subjectType}/{evt.subjectId}
        </div>

        {/* Confidence */}
        <div style={{ fontSize: 11, fontFamily: F.mono, color: evt.confidence >= 0.9 ? C.neon : C.amber, textAlign: 'center' }}>
          {(evt.confidence * 100).toFixed(0)}%
        </div>

        {/* Compliance level */}
        <StatusBadge
          label={isL1 ? 'L1 VERIFIED' : 'L0 PENDING'}
          color={isL1 ? C.neon : C.coral}
        />

        {/* Hash (truncated) */}
        <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, textAlign: 'right' }}>
          {evt.signatureHash ? evt.signatureHash.slice(0, 12) + '...' : '---'}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{
          padding: '0 14px 14px', borderTop: `1px solid ${C.faint}`,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, paddingTop: 14,
        }}>
          {/* Left: chain + attributions */}
          <div>
            <DetailSection label="CHAIN HASHES">
              <HashRow label="This" hash={evt.signatureHash} />
              <HashRow label="Prev" hash={evt.previousEventHash} />
            </DetailSection>

            <DetailSection label="ATTRIBUTIONS">
              {(() => {
                try {
                  const attrs = JSON.parse(evt.attributions) as { principalType: string; principalId: string; role: string; confidence: number }[];
                  return attrs.length > 0 ? attrs.map((a, i) => (
                    <div key={i} style={{ fontSize: 10, fontFamily: F.mono, color: C.muted, marginBottom: 2 }}>
                      {a.principalType}:{a.principalId} ({a.role}) — {(a.confidence * 100).toFixed(0)}%
                    </div>
                  )) : <span style={{ fontSize: 10, color: C.dim }}>None</span>;
                } catch { return <span style={{ fontSize: 10, color: C.dim }}>---</span>; }
              })()}
            </DetailSection>

            {/* Promote action */}
            {!isL1 && (
              <button
                onClick={(e) => { e.stopPropagation(); onPromote(); }}
                disabled={promoteLoading}
                style={{
                  background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
                  fontFamily: F.mono, fontSize: 10, fontWeight: 600, padding: '6px 16px',
                  borderRadius: 6, cursor: 'pointer', marginTop: 10, opacity: promoteLoading ? 0.5 : 1,
                }}
              >
                {promoteLoading ? 'PROMOTING...' : 'PROMOTE TO L1'}
              </button>
            )}
            {isL1 && evt.promotedAt && (
              <div style={{ fontSize: 9, fontFamily: F.mono, color: C.neon, marginTop: 8 }}>
                Promoted {new Date(evt.promotedAt).toLocaleString()} by user #{evt.promotedByUserId}
              </div>
            )}
          </div>

          {/* Right: payload */}
          <div>
            <DetailSection label="PAYLOAD">
              <pre style={{
                fontSize: 10, fontFamily: F.mono, color: C.muted, background: 'rgba(0,0,0,0.3)',
                borderRadius: 6, padding: 10, overflow: 'auto', maxHeight: 200, margin: 0,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>
                {(() => {
                  try { return JSON.stringify(JSON.parse(evt.payload), null, 2); }
                  catch { return evt.payload; }
                })()}
              </pre>
            </DetailSection>

            <DetailSection label="EXPERTISE">
              {(() => {
                try {
                  const uris = JSON.parse(evt.expertiseUris) as string[];
                  return uris.length > 0 ? uris.map((u, i) => (
                    <div key={i} style={{ fontSize: 10, fontFamily: F.mono, color: C.violet, marginBottom: 2 }}>{u}</div>
                  )) : <span style={{ fontSize: 10, color: C.dim }}>None</span>;
                } catch { return <span style={{ fontSize: 10, color: C.dim }}>---</span>; }
              })()}
            </DetailSection>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper components ─────────────────────────────────────────────────────

function CategoryPill({ label, count, color, total }: { label: string; count: number; color: string; total: number }) {
  const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      <span style={{ fontSize: 10, fontFamily: F.mono, color: C.muted }}>{label}</span>
      <span style={{ fontSize: 10, fontFamily: F.mono, fontWeight: 600, color }}>{count}</span>
      <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}>({pct}%)</span>
    </div>
  );
}

function DetailSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function HashRow({ label, hash }: { label: string; hash: string }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
      <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, width: 30 }}>{label}:</span>
      <span style={{ fontSize: 9, fontFamily: F.mono, color: hash ? C.cyan : C.dim, wordBreak: 'break-all' }}>
        {hash || 'GENESIS'}
      </span>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const filterSelectStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`, borderRadius: 6,
  padding: '5px 10px', color: C.text, fontFamily: F.mono, fontSize: 10, outline: 'none',
};

function pageBtnStyle(enabled: boolean): React.CSSProperties {
  return {
    background: enabled ? `${C.blue}15` : 'transparent',
    border: `1px solid ${enabled ? `${C.blue}30` : C.faint}`,
    color: enabled ? C.blue : C.dim,
    fontFamily: F.mono, fontSize: 10, padding: '6px 16px', borderRadius: 6,
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}
